import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import LoginButton from './LoginButton';
import LogoutButton from './LogoutButton';
import { MoonIcon, SunIcon, MenuIcon, XIcon } from '@heroicons/react/solid';
import { useTheme } from '../contexts/ThemeContext';

function Navbar() {
  const { isAuthenticated, user, isLoading, loginWithRedirect } = useAuth0();
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    // Check if user has a preference saved in localStorage
    const savedTheme = localStorage.getItem('theme');
    // Check if user prefers dark mode in their OS settings
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    return savedTheme === 'dark' || (!savedTheme && prefersDark);
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // Function to navigate and scroll to top smoothly
  const navigateToTop = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false); // Close mobile menu on navigation
  };

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Theme effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Remove the local toggleTheme function since we're using the one from context
  useEffect(() => {
    // Sync local darkMode state with theme context
    setDarkMode(theme === 'dark');
  }, [theme]);

  // Express Analytics inspired colors and styling
  const navBgClass = scrolled
    ? (theme === 'dark' ? 'bg-gray-900 shadow-lg py-3' : 'bg-white shadow-lg py-3')
    : (theme === 'dark' ? 'bg-gray-900 py-4' : 'bg-white py-4');

  const linkTextColor = theme === 'dark' ? 'text-gray-300 hover:text-red-400 font-semibold' : 'text-gray-700 hover:text-red-600 font-semibold';
  const activeLinkClass = theme === 'dark' ? 'text-red-400 font-semibold' : 'text-red-600 font-semibold';

  const mobileMenuVariants = {
    open: { opacity: 1, y: 0, transition: { type: "tween", duration: 0.3, ease: "easeInOut" } },
    closed: { opacity: 0, y: "-100%", transition: { type: "tween", duration: 0.3, ease: "easeInOut" } },
  };

  // Helper to create link class names
  const getLinkClassName = (path) => {
    return `font-medium transition-colors duration-200 ${location.pathname === path || (path === '/models' && location.pathname.includes('/model')) ? activeLinkClass : linkTextColor}`;
  };

  const getMobileLinkClassName = (path) => {
    return `block px-3 py-2 rounded-md text-base font-semibold ${location.pathname === path || (path === '/models' && location.pathname.includes('/model')) ? 'text-red-600 bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`;
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        {/* Logo Section */}
        <div className="flex items-center">
          <button onClick={() => navigateToTop('/')} className="flex items-center">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white font-bold text-xl">E</span>
            </div>
            <div className="flex flex-col">
              <span className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Express Analytics
              </span>
              <span className={`text-xs -mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                AI-Powered Smarter Marketing
              </span>
            </div>
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <button
            onClick={() => navigateToTop('/features')}
            className={getLinkClassName('/features')}
          >
            Pricing
          </button>
          <button
            onClick={() => navigateToTop('/support')}
            className={getLinkClassName('/support')}
          >
            Support
          </button>
          {isAuthenticated && (
            <button
              onClick={() => navigateToTop('/models')}
              className={getLinkClassName('/models')}
            >
              Models
            </button>
          )}
        </div>

        {/* Right Side Actions */}
        <div className="hidden md:flex items-center space-x-4">
          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <SunIcon className="h-5 w-5 text-yellow-500" /> : <MoonIcon className="h-5 w-5 text-gray-500" />}
          </button>

          {isLoading ? (
            <div className="flex items-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-red-600 rounded-full animate-spin mr-2"></div>
              <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Loading...</span>
            </div>
          ) : isAuthenticated ? (
            <div className="relative group">
              <div
                className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 cursor-pointer"
                onClick={() => navigateToTop('/models')}
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-red-600 flex items-center justify-center text-white font-bold">
                    {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className={`absolute right-0 mt-2 w-64 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg py-2 z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300`}>
                <div className={`px-4 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                  <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-800'} truncate`}>{user?.name || user?.email}</p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} truncate`}>{user?.email}</p>
                </div>
                <div className="px-4 py-2">
                  <LogoutButton />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <LoginButton />
              <button
                onClick={() => window.location.href = 'https://oyster-lite.us.auth0.com/u/signup?state=hKFo2SBYUEFSNUNhaEYwSDRneGtWbXZEQ2tzN0lpUlAzMV81RqFur3VuaXZlcnNhbC1sb2dpbqN0aWTZIGZaamZtT2EtemZlNTg3a1lVWEs0em9sb1hwcUJhNi1Eo2NpZNkgUVlzcURQeERjTmhxbk5hdkJHTTlFZ215VnRqUkZwa3k'}
                className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 transition-colors duration-200"
              >
                Sign Up
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center space-x-2">
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <SunIcon className="h-5 w-5 text-yellow-500" /> : <MoonIcon className="h-5 w-5 text-gray-500" />}
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-600 ${theme === 'dark' ? 'text-gray-300 hover:text-red-400' : 'text-gray-700 hover:text-red-600'}`}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className={`md:hidden absolute top-full left-0 right-0 shadow-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <button onClick={() => navigateToTop('/features')} className={getMobileLinkClassName('/features')}>Pricing</button>
              <button onClick={() => navigateToTop('/support')} className={getMobileLinkClassName('/support')}>Support</button>
              {isAuthenticated && (
                <>
                  <button onClick={() => navigateToTop('/models')} className={getMobileLinkClassName('/models')}>Models</button>
                </>
              )}
            </div>
            {/* Auth buttons or User Info in Mobile Menu */}
            <div className={`pt-4 pb-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              {isLoading ? (
                <div className="px-5 flex items-center">
                  <div className="w-5 h-5 border-t-2 border-b-2 border-red-600 rounded-full animate-spin mr-2"></div>
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Loading...</span>
                </div>
              ) : isAuthenticated ? (
                <div className="px-5">
                  <div
                    className="flex items-center mb-3 cursor-pointer"
                    onClick={() => navigateToTop('/models')}
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 flex-shrink-0 mr-3">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-red-600 flex items-center justify-center text-white font-bold">
                          {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className={`text-base font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{user?.name}</p>
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
                    </div>
                  </div>
                  <LogoutButton />
                </div>
              ) : (
                <div className="px-5 space-y-2">
                  <LoginButton />
                  <button
                    onClick={() => window.location.href = 'https://oyster-lite.us.auth0.com/u/signup?state=hKFo2SBYUEFSNUNhaEYwSDRneGtWbXZEQ2tzN0lpUlAzMV81RqFur3VuaXZlcnNhbC1sb2dpbqN0aWTZIGZaamZtT2EtemZlNTg3a1lVWEs0em9sb1hwcUJhNi1Eo2NpZNkgUVlzcURQeERjTmhxbk5hdkJHTTlFZ215VnRqUkZwa3k'}
                    className="w-full bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 transition-colors duration-200"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

export default Navbar;
